import {
  Activity,
  BarChart3,
  BookOpenText,
  Check,
  ChevronRight,
  CircleDollarSign,
  History,
  Info,
  ListChecks,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Ticket,
  type LucideIcon
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DataHealthRow, ExplanationLine, GameAdvice, GameKey, TicketHistoryRecord } from "../shared/types";
import { buildPerformanceSummary } from "../shared/performance";
import {
  defaultAppSettings,
  checkTicketResults,
  loadAppBundle,
  getAuthStatus,
  loginWithCode,
  markAllTicketsPlayed,
  markTicketPlayed,
  restoreLatestSnapshot,
  runLiveUpdate,
  runBacktest,
  saveTicketResult,
  ApiError,
  type AppBundle,
  type AppSettings
} from "./api";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });
const percent = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 3 });

type TabKey = "tickets" | "details" | "strategy" | "history" | "settings";
type HistoryFilter = "all" | "active" | "checked" | "prize";

const SETTINGS_KEY = "lotto-settings";
const TOKEN_KEY = "lotto-token";
const LEGACY_SETTINGS_KEY = "lotto-edge-settings";
const LEGACY_TOKEN_KEY = "lotto-edge-token";

function loadSettings(): AppSettings {
  const raw = localStorage.getItem(SETTINGS_KEY) ?? localStorage.getItem(LEGACY_SETTINGS_KEY);
  if (!raw) return defaultAppSettings;
  try {
    const loaded = { ...defaultAppSettings, ...JSON.parse(raw) } as AppSettings;
    if (loaded.candidate_count < defaultAppSettings.candidate_count) {
      loaded.candidate_count = defaultAppSettings.candidate_count;
      loaded.keep_top = defaultAppSettings.keep_top;
    }
    return loaded;
  } catch {
    return defaultAppSettings;
  }
}

function readToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(LEGACY_TOKEN_KEY) ?? "";
}

function formatShortDate(date: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(`${date}T12:00:00`));
}

function formatObserved(value: string | null): string {
  if (!value) return "No live timestamp";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function formatOdds(probability: number): string {
  if (!Number.isFinite(probability) || probability <= 0) return "Not measurable";
  return `1 in ${compact.format(Math.round(1 / probability))}`;
}

function gradeLabel(value: string): string {
  return value.replace(/_/g, " ");
}

function healthLabel(value: DataHealthRow["status"]): string {
  if (value === "good") return "Ready";
  if (value === "watch") return "Building";
  return "Needs data";
}

function healthTone(value: DataHealthRow["status"]): ExplanationLine["tone"] {
  if (value === "good") return "good";
  if (value === "needs_data") return "caution";
  return "neutral";
}

function friendlyEvidenceStatus(value: string): string {
  if (value.includes("human_pick")) return "Crowd learning active";
  if (value.includes("active")) return "External factors used";
  return "Held until proven";
}

function shortSourceLabel(advice: GameAdvice): string {
  if (advice.data_status.jackpot_source_kind === "live") return "Live prize";
  if (advice.data_status.jackpot_source_kind === "fixed") return "Fixed prize";
  return "Needs source";
}

function playLabel(advice: GameAdvice): string {
  return advice.profit_stance === "PLAY" ? "Value play" : "Play optional";
}

function ticketReason(advice: GameAdvice): string {
  const baseline = advice.ticket.single_ticket_score_receipt?.random_baseline_comparison;
  const field = advice.ticket.single_ticket_score_receipt?.simulation_field_comparison;
  const noBias = Number(advice.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0;
  if (field) {
    if (noBias) return "Fair-draw ticket for the next drawing. No historical number signal earned influence today.";
    return `Top current profile among ${compact.format(field.tickets_tested)} tested legal tickets. Same official jackpot odds.`;
  }
  if (baseline) {
    const value = money2.format(baseline.value_delta);
    return `Same jackpot odds as random, with a ${value} value difference and ${baseline.payout_sharing_delta >= 0 ? "stronger" : "similar"} sharing profile.`;
  }
  const source = advice.data_status.jackpot_source_kind === "fallback" ? "available prize data" : shortSourceLabel(advice).toLowerCase();
  return `Chosen from ${source}, official odds, and payout-sharing checks.`;
}

function baselineVerdict(advice: GameAdvice): string {
  const baseline = advice.ticket.single_ticket_score_receipt?.random_baseline_comparison;
  if (Number(advice.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0) return "Same odds";
  if (!baseline) return "Baseline pending";
  if (baseline.verdict === "stronger") return "Stronger than random";
  if (baseline.verdict === "weaker") return "Close to random";
  return "Similar to random";
}

function valueDeltaLabel(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${money2.format(value)}`;
}

function sourceStateText(advice: GameAdvice): string {
  if (advice.data_status.jackpot_source_kind === "fallback") return "Needs fresh prize source";
  return advice.data_status.jackpot_source_label;
}

type SignalTone = "good" | "neutral" | "caution";
type OutsideGameSignal = AppBundle["advisor"]["outside_signal_audit"]["games"][number];

function outsideSignalForGame(bundle: AppBundle, gameKey: GameKey): OutsideGameSignal | undefined {
  return bundle.advisor.outside_signal_audit?.games.find((game) => game.game_key === gameKey);
}

function sourceOverallLabel(bundle: AppBundle): { value: string; detail: string; tone: SignalTone } {
  const status = bundle.advisor.source_freshness.overall_status;
  if (status === "current") {
    return { value: "Current", detail: "Prize, results, and recent source checks are fresh.", tone: "good" };
  }
  if (status === "stale") {
    return { value: "Sync needed", detail: "Use Sync now to refresh stale source rows.", tone: "neutral" };
  }
  return { value: "Needs source", detail: "One or more public sources still need a successful pull.", tone: "caution" };
}

function benchmarkSignal(bundle: AppBundle): { value: string; detail: string; tone: SignalTone } {
  const latest = bundle.backtests[0]?.summary;
  if (!latest) return { value: "Replay pending", detail: "Run a past-draw replay when you want a new benchmark.", tone: "neutral" };
  const grade = latest.benchmark_grade ?? "C";
  return {
    value: `${grade} replay`,
    detail: latest.benchmark_verdict ?? "Recent past draws are replayed against simple challengers.",
    tone: grade === "A" || grade === "B" ? "good" : grade === "C" ? "neutral" : "caution"
  };
}

function processSignal(bundle: AppBundle): { value: string; detail: string; tone: SignalTone } {
  const audit = bundle.advisor.ai_audit;
  if (!audit) return { value: "Building", detail: "Decision receipts will fill in after the next run.", tone: "neutral" };
  return {
    value: `${audit.grade} process`,
    detail: audit.label,
    tone: audit.grade === "A" || audit.grade === "A-" || audit.grade === "B+" ? "good" : audit.grade.startsWith("B") ? "neutral" : "caution"
  };
}

function numberPolicyLabel(advice: GameAdvice, signal?: OutsideGameSignal): string {
  const noBias = Number(advice.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0;
  if (noBias) return "Fair draw";
  if (signal?.number_status === "active") return "Signal";
  return "Scored";
}

function valuePolicyLabel(advice: GameAdvice, signal?: OutsideGameSignal): string {
  if (advice.profit_stance === "PLAY") return "Positive";
  if (signal?.economics_status === "active") return "Used";
  return "Checked";
}

function sharingPolicyLabel(signal?: OutsideGameSignal): string {
  if (signal?.split_status === "active") return "Used";
  return "Checked";
}

function gradeTone(value: string): SignalTone {
  if (/^(A|B)/.test(value)) return "good";
  if (/^C/.test(value)) return "neutral";
  return "caution";
}

function previousAdvisorSnapshot(bundle: AppBundle) {
  return bundle.snapshots.find((snapshot) => {
    const payload = snapshot.payload as AppBundle["advisor"] | null;
    return payload?.recommendation_id && payload.recommendation_id !== bundle.advisor.recommendation_id && Array.isArray(payload.games);
  });
}

function changeReceiptLines(bundle: AppBundle): ExplanationLine[] {
  const previous = previousAdvisorSnapshot(bundle);
  const current = bundle.advisor;
  if (!previous?.payload) {
    return [
      {
        label: "Prior receipt",
        value: "Not yet",
        detail: "The next saved run will show exactly what changed in tickets, prizes, and draw dates.",
        tone: "neutral"
      }
    ];
  }
  const prior = previous.payload as AppBundle["advisor"];
  const lines: ExplanationLine[] = [];
  for (const game of current.games) {
    const old = prior.games.find((item) => item.game_key === game.game_key);
    if (!old) continue;
    const numberChanged = old.ticket.ticket.compact !== game.ticket.ticket.compact;
    const drawChanged = old.draw_date !== game.draw_date;
    const prizeChanged = Math.abs(Number(old.jackpot_cash ?? 0) - Number(game.jackpot_cash ?? 0)) > 1;
    if (numberChanged || drawChanged || prizeChanged) {
      const detail = [
        numberChanged ? "numbers changed" : "",
        drawChanged ? `draw moved to ${formatShortDate(game.draw_date)}` : "",
        prizeChanged ? `cash prize is now ${money.format(game.jackpot_cash)}` : ""
      ]
        .filter(Boolean)
        .join("; ");
      lines.push({
        label: game.game_name,
        value: numberChanged ? "Updated" : "Inputs changed",
        detail,
        tone: numberChanged || prizeChanged ? "good" : "neutral"
      });
    }
  }
  if (!lines.length) {
    lines.push({
      label: "Current draw",
      value: "No change",
      detail: `Same ticket set as the saved receipt from ${formatObserved(prior.generated_at)}.`,
      tone: "neutral"
    });
  }
  return lines.slice(0, 3);
}

function compactToParts(value: string | null | undefined): { whites: number[]; bonus: number } | null {
  if (!value) return null;
  const [whiteRaw, bonusRaw] = value.split("+");
  const whites = whiteRaw?.split("-").map(Number).filter(Number.isFinite) ?? [];
  const bonus = Number(bonusRaw);
  if (whites.length !== 5 || !Number.isFinite(bonus)) return null;
  return { whites, bonus };
}

function NumberBall({ value, bonus = false }: { value: number; bonus?: boolean }) {
  return <span className={bonus ? "number-ball bonus" : "number-ball"}>{String(value).padStart(2, "0")}</span>;
}

function StatPill({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="stat-pill">
      <Icon size={17} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SignalChip({ label, value, tone = "neutral" }: { label: string; value: string; tone?: SignalTone }) {
  return (
    <span className={`signal-chip tone-${tone}`}>
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}

function DecisionHero({ bundle, playedCount, allTracked }: { bundle: AppBundle; playedCount: number; allTracked: boolean }) {
  const source = sourceOverallLabel(bundle);
  const replay = benchmarkSignal(bundle);
  const process = processSignal(bundle);
  return (
    <section className="advisor-hero decision-hero">
      <div className="decision-copy">
        <span className="eyebrow">Next draws</span>
        <h1>Today's three tickets</h1>
        <p>
          One ticket for Powerball, Mega Millions, and Millionaire for Life. Numbers are locked to the next draw with the exact receipt used today.
        </p>
        <p className="hero-best">Top game today: {bundle.advisor.best_game_name}</p>
      </div>
      <div className="hero-cost">
        <span>Total</span>
        <strong>{money2.format(bundle.advisor.total_cost)}</strong>
        <small>{playedCount} marked played / 3</small>
      </div>
      <div className="decision-signal-strip" aria-label="Decision status">
        <SignalChip label="Process" value={process.value} tone={process.tone} />
        <SignalChip label="Data" value={source.value} tone={source.tone} />
        <SignalChip label="Replay" value={replay.value} tone={replay.tone} />
        <SignalChip label="Tracking" value={allTracked ? "Played" : "Ready"} tone={allTracked ? "good" : "neutral"} />
      </div>
    </section>
  );
}

function ChangeReceiptPanel({ bundle }: { bundle: AppBundle }) {
  return (
    <section className="change-receipt-panel">
      <div className="panel-heading compact-heading">
        <ShieldCheck size={19} />
        <div>
          <span>Receipt</span>
          <h2>What changed</h2>
        </div>
      </div>
      <div className="change-list">
        {changeReceiptLines(bundle).map((line) => (
          <LineItem line={line} key={`${line.label}:${line.value}`} />
        ))}
      </div>
    </section>
  );
}

function TicketSignalRow({ bundle, advice }: { bundle: AppBundle; advice: GameAdvice }) {
  const signal = outsideSignalForGame(bundle, advice.game_key);
  return (
    <div className="ticket-signal-row" aria-label={`${advice.game_name} selection checks`}>
      <span>
        <small>Numbers</small>
        <strong>{numberPolicyLabel(advice, signal)}</strong>
      </span>
      <span>
        <small>Value</small>
        <strong>{valuePolicyLabel(advice, signal)}</strong>
      </span>
      <span>
        <small>Sharing</small>
        <strong>{sharingPolicyLabel(signal)}</strong>
      </span>
    </div>
  );
}

function ReceiptAtGlancePanel({ bundle, advice }: { bundle: AppBundle; advice: GameAdvice }) {
  const signal = outsideSignalForGame(bundle, advice.game_key);
  const replay = benchmarkSignal(bundle);
  const receipt = advice.ticket.single_ticket_score_receipt;
  return (
    <section className="trust-panel receipt-at-glance">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Decision receipt</span>
          <h2>{advice.game_name}</h2>
        </div>
      </div>
      <div className="data-grid trust-grid">
        <LineItem
          line={{
            label: "Top-prize odds",
            value: formatOdds(advice.recommendation.jackpot_probability),
            detail: "Same official jackpot chance as any other valid ticket.",
            tone: "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Number policy",
            value: numberPolicyLabel(advice, signal),
            detail:
              Number(advice.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0
                ? "Historical number tests are held, so the app does not force a pattern."
                : "A number signal earned influence before this draw.",
            tone: signal?.number_status === "active" ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Prize input",
            value: shortSourceLabel(advice),
            detail: `${advice.data_status.jackpot_source_label}. Latest pull: ${formatObserved(advice.data_status.jackpot_observed_at)}.`,
            tone: advice.data_status.jackpot_source_kind === "fallback" ? "caution" : "good"
          }}
        />
        <LineItem
          line={{
            label: "Search receipt",
            value: receipt ? receipt.simulation_field_comparison.confidence_label.replace(" process confidence", "") : "Not recorded",
            detail: receipt ? receipt.simulation_field_comparison.plain_english : "The next run will save a ticket-search receipt.",
            tone: receipt ? gradeTone(bundle.advisor.ai_audit?.grade ?? "C") : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Replay",
            value: replay.value,
            detail: replay.detail,
            tone: replay.tone
          }}
        />
        <LineItem
          line={{
            label: "Receipt ID",
            value: advice.recommendation_id.slice(-6),
            detail: "This ID ties the ticket to the saved prize inputs and explanation.",
            tone: "neutral"
          }}
        />
      </div>
    </section>
  );
}

function AiAuditPanel({ bundle }: { bundle: AppBundle }) {
  const audit = bundle.advisor.ai_audit;
  const factors = audit?.factors ?? [];
  return (
    <section className="audit-panel ai-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>App grade</span>
          <h2>{audit ? `${audit.grade} ${audit.label}` : "Building"}</h2>
        </div>
      </div>
      <p className="plain-copy">
        {audit
          ? `${audit.plain_english} A-F grades refresh with the recommendations.`
          : "The app searches legal tickets, checks learned signals against history, and keeps the receipt attached to each recommendation."}
      </p>
      <div className="ai-factor-grid">
        {factors.map((factor) => (
          <article className={`ai-factor status-${factor.status}`} key={factor.key}>
            <div>
              <span>{factor.label}</span>
              <strong>{factor.grade}</strong>
            </div>
            <p>{factor.detail}</p>
          </article>
        ))}
      </div>
      {audit?.next_upgrade && <p className="performance-note">{audit.next_upgrade}</p>}
    </section>
  );
}

function LineItem({ line }: { line: ExplanationLine }) {
  return (
    <article className={`line-item tone-${line.tone}`}>
      <div>
        <span>{line.label}</span>
        <strong>{line.value}</strong>
      </div>
      <p>{line.detail}</p>
    </article>
  );
}

function trackedKey(advice: GameAdvice): string {
  return `${advice.game_key}:${advice.draw_date}:${advice.ticket.ticket.compact}`;
}

function DataStatusPanel({ advice }: { advice: GameAdvice }) {
  const status = advice.data_status;
  return (
    <section className="data-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Data used today</span>
          <h2>{sourceStateText(advice)}</h2>
        </div>
      </div>
      <div className="data-grid">
        <LineItem
          line={{
            label: "Prize source",
            value: status.jackpot_source_label,
            detail: `Latest pull: ${formatObserved(status.jackpot_observed_at)}.`,
            tone: status.jackpot_source_kind === "fallback" ? "caution" : "good"
          }}
        />
        <LineItem
          line={{
            label: "Result history",
            value: `${status.draw_history_count} draws`,
            detail: status.latest_draw_date
              ? `Used for current-rule model tests and result checks. Latest result: ${status.latest_draw_date}${status.latest_winning_numbers ? ` (${status.latest_winning_numbers})` : ""}.`
              : "No results have been stored for this game yet.",
            tone: status.draw_history_count > 0 ? "good" : "caution"
          }}
        />
        <LineItem
          line={{
            label: "Sales labels",
            value: `${status.sales_count} records`,
            detail: status.sales_latest_date ? `Used only after testing. Latest label: ${status.sales_latest_date}.` : status.learning_status,
            tone: status.sales_count >= 60 ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Sharing labels",
            value: `${status.tier_winner_count} records`,
            detail: status.tier_winner_latest_date
              ? `Used only after testing. Latest label: ${status.tier_winner_latest_date}.`
              : "Needed before payout-sharing estimates can learn from real winner counts.",
            tone: status.tier_winner_count >= 120 ? "good" : "neutral"
          }}
        />
      </div>
    </section>
  );
}

function SourceFreshnessPanel({ bundle }: { bundle: AppBundle }) {
  const summary = bundle.advisor.source_freshness;
  const statusLabel = summary.overall_status === "current" ? "Data current" : summary.overall_status === "stale" ? "Fresh pull recommended" : "Some data still missing";
  return (
    <section className="freshness-panel">
      <div className="panel-heading">
        <Activity size={20} />
        <div>
          <span>Manual updates</span>
          <h2>{statusLabel}</h2>
        </div>
      </div>
      <div className="freshness-list">
        {summary.rows.map((row) => (
          <article className={`freshness-row status-${row.status}`} key={row.key}>
            <div>
              <strong>{row.label}</strong>
              <span>{row.detail}</span>
            </div>
            <small>{row.last_success_at ? formatObserved(row.last_success_at) : "Not recorded"}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function SourceQualityPanel({ bundle }: { bundle: AppBundle }) {
  return (
    <section className="quality-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Coverage</span>
          <h2>{gradeLabel(bundle.sourceQuality.overall_grade)}</h2>
        </div>
      </div>
      <div className="quality-grid">
        {bundle.sourceQuality.rows.map((row) => (
          <article className={`quality-row grade-${row.grade}`} key={row.key}>
            <div>
              <strong>{row.label}</strong>
              <span>{row.detail}</span>
            </div>
            <div className="quality-stats">
              <small>{gradeLabel(row.grade)}</small>
              <small>{row.record_count ? compact.format(row.record_count) : "No labels"}</small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function statusWord(status: "active" | "held"): string {
  return status === "active" ? "Active" : "Held";
}

function outsideStatusWord(status: "active" | "held" | "building"): string {
  if (status === "active") return "Active";
  if (status === "held") return "Held";
  return "Building";
}

function OutsideSignalsPanel({ bundle }: { bundle: AppBundle }) {
  const audit = bundle.advisor.outside_signal_audit;
  if (!audit) return null;
  return (
    <section className="outside-signal-panel">
      <div className="panel-heading">
        <Activity size={20} />
        <div>
          <span>Outside signals</span>
          <h2>Weather, date, jackpot context</h2>
        </div>
      </div>
      <p className="plain-copy">{audit.headline}</p>
      <div className="outside-signal-grid">
        {audit.games.map((row) => (
          <article className="outside-signal-card" key={row.game_key}>
            <div className="learning-card-top">
              <div>
                <strong>{row.game_name}</strong>
                <span>{row.best_factor_label}</span>
              </div>
              <small>{compact.format(row.tested_draws)} draws</small>
            </div>
            <div className="outside-status-row">
              <span className={`learning-pill status-${row.number_status}`}>Numbers {outsideStatusWord(row.number_status)}</span>
              <span className={`learning-pill status-${row.economics_status}`}>Value {outsideStatusWord(row.economics_status)}</span>
              <span className={`learning-pill status-${row.split_status}`}>Sharing {outsideStatusWord(row.split_status)}</span>
            </div>
            <p>{row.number_detail}</p>
            <p>{row.economics_detail}</p>
          </article>
        ))}
      </div>
      <p className="learning-note">{audit.principle}</p>
    </section>
  );
}

function LearningScopePanel({ bundle }: { bundle: AppBundle }) {
  const scope = bundle.advisor.learning_scope;
  if (!scope) return null;
  return (
    <section className="learning-scope-panel">
      <div className="panel-heading">
        <BookOpenText size={20} />
        <div>
          <span>Learning base</span>
          <h2>Public history, not just your tickets</h2>
        </div>
      </div>
      <p className="plain-copy">{scope.headline}</p>
      <div className="learning-scope-grid">
        {scope.games.map((row) => (
          <article className="learning-scope-card" key={row.game_key}>
            <div className="learning-card-top">
              <div>
                <strong>{row.game_name}</strong>
                <span>Current rules from {row.current_rule_start}</span>
              </div>
              <small>{row.latest_winning_numbers ?? "No result yet"}</small>
            </div>
            <div className="learning-metrics">
              <span>Draws</span>
              <strong>{compact.format(row.draw_history_count)}</strong>
              <span>Sales</span>
              <strong>{compact.format(row.sales_label_count)}</strong>
              <span>Winners</span>
              <strong>{compact.format(row.winner_label_count)}</strong>
            </div>
            <div className="learning-status-row">
              <span className={`learning-pill status-${row.draw_bias_status}`}>Draw {statusWord(row.draw_bias_status)}</span>
              <span className={`learning-pill status-${row.sales_model_status}`}>Sales {statusWord(row.sales_model_status)}</span>
              <span className={`learning-pill status-${row.crowd_model_status}`}>Crowd {statusWord(row.crowd_model_status)}</span>
            </div>
            <p>{row.plain_english}</p>
          </article>
        ))}
      </div>
      <p className="learning-note">{scope.rule_scope_note}</p>
      <p className="learning-note">{scope.personal_scope_note}</p>
    </section>
  );
}

function DataHealthPanel({ bundle }: { bundle: AppBundle }) {
  const summary = bundle.dataHealth;
  const learningRows = summary.rows.filter((row) => row.kind === "sales_labels" || row.kind === "winner_labels");
  const sourceRows = summary.rows.filter((row) => row.kind === "prize" || row.kind === "results" || row.kind === "sync");
  return (
    <section className="data-health-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Data health</span>
          <h2>{summary.overall_status === "good" ? "Ready today" : summary.overall_status === "watch" ? "Usable while learning" : "Needs source attention"}</h2>
        </div>
      </div>
      <p className="plain-copy">
        Fair draw odds do not change. Extra factors only earn influence after sales and winner labels beat the simple baseline.
      </p>
      <div className="health-readiness">
        <LineItem
          line={{
            label: "Sales learning",
            value: `${summary.label_readiness.sales_ready_games}/3 games`,
            detail: "Needs enough labeled draws before demand can move the recommendation.",
            tone: summary.label_readiness.sales_ready_games === 3 ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Crowd learning",
            value: `${summary.label_readiness.winner_ready_games}/3 games`,
            detail: "Needs enough winner-count labels before sharing behavior can move the recommendation.",
            tone: summary.label_readiness.winner_ready_games === 3 ? "good" : "neutral"
          }}
        />
      </div>
      <div className="health-list">
        {[...sourceRows, ...learningRows].map((row) => (
          <article className={`health-row health-${row.status}`} key={row.key}>
            <div>
              <strong>{row.label}</strong>
              <span>{row.detail}</span>
            </div>
            <div className="health-stats">
              <small>{healthLabel(row.status)}</small>
              <small>
                {row.required_for_learning ? `${compact.format(row.count)} / ${compact.format(row.required_for_learning)}` : row.latest_at ? formatObserved(row.latest_at) : "Not recorded"}
              </small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function SourceAuditPanel({ bundle }: { bundle: AppBundle }) {
  const audit = bundle.sourceAudit;
  return (
    <section className="quality-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Source audit</span>
          <h2>{audit.status === "clear" ? "Sources agree" : audit.status === "critical" ? "Conflict found" : "Review source notes"}</h2>
        </div>
      </div>
      <div className="quality-grid">
        {audit.issues.slice(0, 8).map((issue) => (
          <article className={`quality-row grade-${issue.severity === "critical" ? "missing" : issue.severity === "watch" ? "stale" : "official_page"}`} key={issue.key}>
            <div>
              <strong>{issue.label}</strong>
              <span>{issue.detail}</span>
            </div>
            <div className="quality-stats">
              <small>{issue.severity}</small>
              <small>{issue.latest_at ? formatObserved(issue.latest_at) : "No date"}</small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProjectionPanel({ bundle }: { bundle: AppBundle }) {
  const p = bundle.advisor.play_projection;
  return (
    <section className="trust-panel">
      <div className="panel-heading">
        <Activity size={20} />
        <div>
          <span>Play projection</span>
          <h2>One ticket per game</h2>
        </div>
      </div>
      <div className="data-grid trust-grid">
        <LineItem
          line={{
            label: "Any prize, week",
            value: percent.format(p.one_week_any_prize_probability),
            detail: `${money2.format(p.weekly_cost)} weekly cost at ${p.weekly_ticket_count} tickets.`,
            tone: "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Any prize, year",
            value: percent.format(p.one_year_any_prize_probability),
            detail: p.expected_weeks_to_any_prize ? `Expected wait: about ${p.expected_weeks_to_any_prize.toFixed(1)} weeks.` : p.plain_english,
            tone: "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Jackpot, year",
            value: percent.format(p.one_year_jackpot_probability),
            detail: p.expected_years_to_jackpot ? `Expected wait at this pace is about ${compact.format(p.expected_years_to_jackpot)} years.` : p.plain_english,
            tone: "neutral"
          }}
        />
      </div>
      <p className="performance-note">{p.plain_english}</p>
    </section>
  );
}

function CalibrationPanel({ bundle }: { bundle: AppBundle }) {
  const c = bundle.calibration;
  return (
    <section className="calibration-panel">
      <div className="panel-heading">
        <BarChart3 size={20} />
        <div>
          <span>Record</span>
          <h2>{c.checked_tickets ? `${c.checked_tickets} checked tickets` : "No checked tickets yet"}</h2>
        </div>
      </div>
      <div className="data-grid performance-grid">
        <LineItem
          line={{
            label: "Projected prize hits",
            value: c.expected_any_prizes.toFixed(2),
            detail: `Actual prize tickets: ${c.actual_prizes}. ${c.sample_status}`,
            tone: c.checked_tickets ? "neutral" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Projected return",
            value: money2.format(c.expected_return),
            detail: `Return so far: ${money2.format(c.actual_return)}.`,
            tone: c.actual_return >= c.expected_return && c.checked_tickets ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Return gap",
            value: money2.format(c.return_gap),
            detail: "Positive means actual checked results are above expected return so far.",
            tone: c.return_gap >= 0 && c.checked_tickets ? "good" : "neutral"
          }}
        />
      </div>
    </section>
  );
}

function BacktestPanel({ bundle, busy, onRunBacktest }: { bundle: AppBundle; busy: boolean; onRunBacktest: () => void }) {
  const latest = bundle.backtests[0];
  return (
    <section className="backtest-panel">
      <div className="panel-heading">
        <Activity size={20} />
        <div>
          <span>Past-draw replay</span>
          <h2>{latest ? `${latest.summary.draws_tested} past draws tested` : "No replay run yet"}</h2>
        </div>
      </div>
      {latest ? (
        <div className="data-grid performance-grid">
          <LineItem
            line={{
              label: "Prize tickets",
              value: String(latest.summary.prize_tickets),
              detail: `${percent.format(latest.summary.actual_prize_rate)} of replayed tickets hit a listed prize.`,
              tone: latest.summary.prize_tickets ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Projected return",
              value: money2.format(latest.summary.expected_return),
              detail: `Replay return: ${money2.format(latest.summary.actual_return)}. ${latest.summary.sample_status}`,
              tone: "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Random baseline",
              value: money2.format(latest.summary.baseline_actual_return ?? 0),
              detail: `Gap versus random valid tickets: ${money2.format(latest.summary.model_vs_baseline_return_gap ?? 0)}.`,
              tone: (latest.summary.model_vs_baseline_return_gap ?? 0) > 0 ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Benchmark grade",
              value: latest.summary.benchmark_grade ?? "C",
              detail: latest.summary.benchmark_verdict ?? `Leader: ${latest.summary.champion_label ?? "Too early"}.`,
              tone: "neutral"
            }}
          />
        </div>
      ) : (
        <p className="plain-copy">Replay recent draws using only information that would have been available before each draw.</p>
      )}
      <div className="history-actions">
        <button onClick={onRunBacktest} disabled={busy}>
          <RefreshCw size={17} />
          Run replay
        </button>
      </div>
      {latest?.summary.benchmark_note && <p className="performance-note">{latest.summary.benchmark_note}</p>}
    </section>
  );
}

function TicketCard({
  bundle,
  advice,
  selected,
  tracked,
  onOpenDetails,
  onMarkPlayed
}: {
  bundle: AppBundle;
  advice: GameAdvice;
  selected: boolean;
  tracked: boolean;
  onOpenDetails: () => void;
  onMarkPlayed: () => void;
}) {
  const ticket = advice.ticket.ticket;
  const prizeLabel = advice.game_key === "millionaire_for_life" ? "Top cash prize" : "Cash jackpot";
  const cardClass = ["ticket-card", selected ? "selected" : "", tracked ? "saved-compact" : "", `game-${advice.game_key}`].filter(Boolean).join(" ");
  return (
    <article className={cardClass}>
      <button className="ticket-card-main" onClick={onOpenDetails}>
        <div className="ticket-card-top">
          <div>
            <span className="eyebrow">{formatShortDate(advice.draw_date)} at {advice.draw_time_et} ET</span>
            <h2>{advice.game_name}</h2>
          </div>
          <ChevronRight size={20} />
        </div>
        <div className="ticket-numbers" aria-label={`${advice.game_name} ticket numbers`}>
          {ticket.whites.map((n) => (
            <NumberBall key={n} value={n} />
          ))}
          <NumberBall value={ticket.bonus} bonus />
        </div>
        <div className="card-money-row">
          <div>
            <span>{prizeLabel}</span>
            <strong>{money.format(advice.jackpot_cash)}</strong>
          </div>
          <div>
            <span>Ticket cost</span>
            <strong>{money2.format(advice.ticket_cost)}</strong>
          </div>
        </div>
        <div className="reason-strip">
          <span>{playLabel(advice)}</span>
          <span className={`source-badge source-${advice.data_status.jackpot_source_kind}`}>{shortSourceLabel(advice)}</span>
        </div>
        <TicketSignalRow bundle={bundle} advice={advice} />
        <p className="ticket-rationale">
          {ticketReason(advice)}
        </p>
      </button>
      <button className={tracked ? "played-button tracked" : "played-button"} onClick={onMarkPlayed} disabled={tracked}>
        <Check size={17} />
        <span>{tracked ? "Played" : "Mark played"}</span>
      </button>
    </article>
  );
}

function ExplanationPanel({ advice }: { advice: GameAdvice }) {
  const receipt = advice.ticket.single_ticket_score_receipt;
  const baseline = receipt?.random_baseline_comparison;
  const field = receipt?.simulation_field_comparison;
  const noBias = Number(advice.ticket.feature_summary.jackpot_first_no_bias_selection ?? 0) > 0;
  const baselineParts = compactToParts(baseline?.ticket.compact);
  const usedLines =
    receipt?.model_contributions.filter((line) => ["cash_value", "pattern_guard", "rotation", "source_health"].includes(line.component)) ?? [];
  const ignoredLines =
    receipt?.model_contributions.filter((line) => line.component === "fair_draw" || line.status === "held" || line.component === "personal_record") ?? [];
  return (
    <section className="explain-panel">
      <div className="panel-heading">
        <BookOpenText size={20} />
        <div>
          <span>Why this ticket</span>
          <h2>{noBias ? "Fair-draw receipt" : "Compared with a random ticket"}</h2>
        </div>
      </div>
      <p className="plain-copy">{advice.explanation.plain_english}</p>

      {receipt && baseline && (
        <div className="baseline-panel">
          <div className="baseline-ticket selected-ticket">
            <span>Selected ticket</span>
            <div className="ticket-numbers compact-numbers">
              {advice.ticket.ticket.whites.map((n) => (
                <NumberBall key={n} value={n} />
              ))}
              <NumberBall value={advice.ticket.ticket.bonus} bonus />
            </div>
            <strong>{baselineVerdict(advice)}</strong>
          </div>
          <div className="baseline-ticket">
            <span>Random baseline</span>
            <div className="ticket-numbers compact-numbers">
              {baselineParts ? (
                <>
                  {baselineParts.whites.map((n) => (
                    <NumberBall key={n} value={n} />
                  ))}
                  <NumberBall value={baselineParts.bonus} bonus />
                </>
              ) : (
                <small>{baseline.ticket.compact}</small>
              )}
            </div>
            <strong>Same jackpot odds</strong>
          </div>
        </div>
      )}

      {receipt && (
        <div className="data-grid score-receipt-grid">
          <LineItem
            line={{
              label: "Jackpot odds",
              value: formatOdds(receipt.physical_draw_odds),
              detail: "Exactly the same top-prize chance as the random baseline.",
              tone: "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Modeled value gap",
              value: baseline ? valueDeltaLabel(baseline.value_delta) : "Not recorded",
              detail: "Difference versus the random baseline after tax, prize value, sharing, and ticket cost.",
              tone: baseline && baseline.value_delta > 0 ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Sharing gap",
              value: baseline ? baseline.payout_sharing_delta.toFixed(4) : "Not recorded",
              detail: "Positive means less expected jackpot sharing than the random baseline.",
              tone: baseline && baseline.payout_sharing_delta > 0 ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Crowd pattern",
              value: `${receipt.public_pick_multiplier.toFixed(2)}x`,
              detail: baseline ? `Random baseline: ${baseline.public_pick_multiplier.toFixed(2)}x. Lower usually means less shared-payout exposure.` : "Lower usually means less shared-payout exposure.",
              tone: baseline && receipt.public_pick_multiplier < baseline.public_pick_multiplier ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: noBias ? "Number policy" : "Model field",
              value: noBias ? "Fair draw" : field ? `${compact.format(field.tickets_tested)} tested` : compact.format(receipt.candidate_search_count),
              detail: noBias ? "No validated ball signal is active, so a bigger search would not create better jackpot odds." : field ? "Searched sample recorded. This is not the whole lottery universe." : "Legal tickets tested for one-ticket play.",
              tone: noBias || (field && field.selected_percentile >= 0.98) ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: noBias ? "Pattern bias" : "What #1 means",
              value: noBias ? "Unbiased" : field ? `Best of ${compact.format(field.tickets_tested)}` : "Not recorded",
              detail: noBias
                ? "No validated ball signal is active, so the number choice is not pattern-biased."
                : field
                  ? "Highest model score in the tested field. It does not mean higher physical draw odds."
                  : "Rank was not recorded.",
              tone: noBias || (field && field.selected_rank <= 3) ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: noBias ? "Search needed" : "Score lift",
              value: noBias ? "No" : field ? valueDeltaLabel(field.selected_score_lift) : "Not recorded",
              detail: noBias ? "With no proven draw signal, the honest jackpot-first choice is a fair legal ticket." : "Difference versus the middle ticket in today's tested field. This is process evidence, not higher draw odds.",
              tone: noBias || (field && field.selected_score_lift > 0) ? "good" : "neutral"
            }}
          />
          <LineItem
            line={{
              label: "Any prize",
              value: percent.format(receipt.any_prize_probability),
              detail: "One-ticket chance across listed prize tiers. This is rule math, not an AI claim.",
              tone: "neutral"
            }}
          />
        </div>
      )}

      {receipt && (
        <div className="decision-columns">
          <div className="decision-column">
            <h3>What mattered</h3>
            <div className="model-contribution-list" aria-label="What mattered">
              {usedLines.map((line) => (
                <article className={`model-contribution status-${line.status}`} key={line.component}>
                  <div>
                    <strong>{line.label}</strong>
                    <span>{line.detail}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <div className="decision-column">
            <h3>What was ignored</h3>
            <div className="model-contribution-list" aria-label="What was ignored">
              {ignoredLines.map((line) => (
                <article className={`model-contribution status-${line.status}`} key={line.component}>
                  <div>
                    <strong>{line.label}</strong>
                    <span>{line.detail}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="trust-box">
        <ShieldCheck size={20} />
        <div>
          <strong>Reality check</strong>
          <p>{advice.explanation.trust_summary}</p>
          <div className="evidence-mini">
            {advice.explanation.evidence.map((line) => (
              <span className={`tone-${line.tone}`} key={line.label}>
                {line.label}: {line.value}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustPanel({ advice, historyCount, checkedCount }: { advice: GameAdvice; historyCount: number; checkedCount: number }) {
  const prizeState = advice.data_status.jackpot_source_kind === "fallback" ? "Needs source" : advice.data_status.jackpot_source_label;
  return (
    <section className="trust-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Reality check</span>
          <h2>What matters</h2>
        </div>
      </div>
      <p className="plain-copy">
        The ticket is saved with the numbers, prize input, and reason shown that day. Completed draws are checked against public results.
      </p>
      <div className="data-grid trust-grid">
        <LineItem
          line={{
            label: "Top-prize odds",
            value: formatOdds(advice.recommendation.jackpot_probability),
            detail: "Same for every valid ticket under the official rules.",
            tone: "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Prize source",
            value: prizeState,
            detail: `${advice.data_status.jackpot_source_label}. Last observed: ${formatObserved(advice.data_status.jackpot_observed_at)}.`,
            tone: advice.data_status.jackpot_source_kind === "fallback" ? "caution" : "good"
          }}
        />
        <LineItem
          line={{
            label: "Extra factors",
            value: friendlyEvidenceStatus(advice.recommendation.external_features_status),
            detail: "Used only when past testing says they help.",
            tone: advice.recommendation.external_features_status.includes("active") ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Your record",
            value: `${checkedCount} checked / ${historyCount} saved`,
            detail: "The ledger shows winning numbers and matches after each draw.",
            tone: historyCount > 0 ? "good" : "neutral"
          }}
        />
      </div>
    </section>
  );
}

function PerformancePanel({ bundle }: { bundle: AppBundle }) {
  const summary = bundle.performance;
  return (
    <section className="performance-panel">
      <div className="panel-heading">
        <ListChecks size={20} />
        <div>
          <span>Record</span>
          <h2>{summary.checked_tickets ? "Measured against real draws" : "Waiting for completed draws"}</h2>
        </div>
      </div>
      <div className="data-grid performance-grid">
        <LineItem
          line={{
            label: "Checked tickets",
            value: `${summary.checked_tickets}/${summary.tracked_tickets}`,
            detail: "Saved tickets are checked against public draw results.",
            tone: summary.checked_tickets ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Prize tickets",
            value: String(summary.prize_tickets),
            detail: `Normal range so far: ${summary.normal_low_prize_tickets}-${summary.normal_high_prize_tickets}.`,
            tone: summary.prize_tickets >= summary.normal_low_prize_tickets && summary.prize_tickets <= summary.normal_high_prize_tickets ? "neutral" : "caution"
          }}
        />
        <LineItem
          line={{
            label: "Hits recorded",
            value: `${summary.total_white_hits} white / ${summary.bonus_hits} bonus`,
            detail: "Your actual matches versus the official winning numbers.",
            tone: summary.checked_tickets ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Return so far",
            value: money2.format(summary.estimated_return),
            detail: summary.best_result ?? "No completed ticket results yet.",
            tone: summary.estimated_return > 0 ? "good" : "neutral"
          }}
        />
        <LineItem
          line={{
            label: "Projected return",
            value: money2.format(summary.expected_return),
            detail: `${summary.sample_status} Profit so far: ${money2.format(summary.actual_profit)}.`,
            tone: summary.checked_tickets ? "neutral" : "neutral"
          }}
        />
      </div>
      {summary.by_game.length > 0 && (
        <div className="game-record-grid">
          {summary.by_game.map((game) => (
            <article className="game-record-row" key={game.game_key}>
              <div>
                <strong>{game.game_name}</strong>
                <span>{game.checked_tickets}/{game.tracked_tickets} checked</span>
              </div>
              <div>
                <strong>{money2.format(game.estimated_return)}</strong>
                <span>{game.best_result ?? "No prize yet"}</span>
              </div>
            </article>
          ))}
        </div>
      )}
      <p className="performance-note">
        This record judges the recommendations against what actually happened after each draw.
      </p>
    </section>
  );
}

function SnapshotLedgerPanel({ bundle }: { bundle: AppBundle }) {
  return (
    <section className="snapshot-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} />
        <div>
          <span>Receipts</span>
          <h2>{bundle.snapshots.length ? `${bundle.snapshots.length} saved reasons` : "None yet"}</h2>
        </div>
      </div>
      <div className="snapshot-list">
        {bundle.snapshots.slice(0, 6).map((snapshot) => (
          <article className="snapshot-row" key={snapshot.recommendation_id}>
            <div>
              <strong>{snapshot.recommendation_id.slice(-6)}</strong>
              <span>{formatObserved(snapshot.generated_at)}</span>
            </div>
            <small>{snapshot.source_freshness?.overall_status === "current" ? "Data current" : "Data review saved"}</small>
          </article>
        ))}
      </div>
      <p className="performance-note">
        Saved reasons keep the exact numbers and inputs used when tickets were generated.
      </p>
    </section>
  );
}

function TicketsTab({
  bundle,
  selectedGame,
  setSelectedGame,
  tracked,
  openDetails,
  onMarkPlayed,
  onTrackAll,
  onRestoreLatest,
  busy
}: {
  bundle: AppBundle;
  selectedGame: GameKey;
  setSelectedGame: (game: GameKey) => void;
  tracked: Set<string>;
  openDetails: (game: GameKey) => void;
  onMarkPlayed: (advice: GameAdvice) => void;
  onTrackAll: () => void;
  onRestoreLatest: () => void;
  busy: boolean;
}) {
  const playedCount = bundle.advisor.games.filter((advice) => tracked.has(trackedKey(advice)) || advice.already_tracked).length;
  const allTracked = playedCount === bundle.advisor.games.length;
  const canRestore = bundle.history.length === 0 && bundle.snapshots.length > 0;
  return (
    <div className="tickets-layout">
      <DecisionHero bundle={bundle} playedCount={playedCount} allTracked={allTracked} />

      <section className="ticket-action-panel">
        <div>
          <strong>{allTracked ? "All three marked played" : "Numbers and receipts saved with this recommendation"}</strong>
          <span>
            {allTracked
              ? "The saved tickets are ready for result checks after the draws close."
              : "Results are checked from the saved receipt. Mark played only if you actually buy the tickets."}
          </span>
        </div>
        <div className="ticket-actions">
          <button className="primary-action" onClick={onTrackAll} disabled={busy || allTracked}>
            <Check size={17} />
            <span>{allTracked ? "Played" : "Mark all played"}</span>
          </button>
          {canRestore && (
            <button className="secondary-action" onClick={onRestoreLatest} disabled={busy}>
              <RefreshCw size={17} />
              <span>Restore latest</span>
            </button>
          )}
        </div>
      </section>

      <ChangeReceiptPanel bundle={bundle} />

      <section className="ticket-card-grid">
        {bundle.advisor.games.map((advice) => (
          <TicketCard
            key={advice.game_key}
            bundle={bundle}
            advice={advice}
            selected={selectedGame === advice.game_key}
            tracked={tracked.has(trackedKey(advice)) || advice.already_tracked}
            onOpenDetails={() => {
              setSelectedGame(advice.game_key);
              openDetails(advice.game_key);
            }}
            onMarkPlayed={() => onMarkPlayed(advice)}
          />
        ))}
      </section>
    </div>
  );
}

function DetailsTab({ bundle, selectedGame, setSelectedGame }: { bundle: AppBundle; selectedGame: GameKey; setSelectedGame: (game: GameKey) => void }) {
  const selected = bundle.advisor.games.find((game) => game.game_key === selectedGame) ?? bundle.advisor.games[0];
  return (
    <div className="why-layout">
      <section className="principle-panel">
        <span className="eyebrow">Why</span>
        <h1>{selected.game_name}</h1>
        <p>
          The receipt behind the selected ticket, including what was used and what was held back.
        </p>
      </section>
      <section className="game-switcher">
        {bundle.advisor.games.map((advice) => (
          <button className={advice.game_key === selectedGame ? "selected" : ""} key={advice.game_key} onClick={() => setSelectedGame(advice.game_key)}>
            {advice.game_name}
          </button>
        ))}
      </section>
      <ReceiptAtGlancePanel bundle={bundle} advice={selected} />
      <ExplanationPanel advice={selected} />
    </div>
  );
}

function StrategyTab({
  bundle,
  busy,
  onRunBacktest
}: {
  bundle: AppBundle;
  busy: boolean;
  onRunBacktest: () => void;
}) {
  return (
    <div className="strategy-layout">
      <section className="principle-panel">
        <span className="eyebrow">Review</span>
        <h1>How the app is graded</h1>
        <p>
          The grade looks at public source freshness, saved history, automation, replay, and whether every ticket has a readable receipt.
        </p>
      </section>
      <AiAuditPanel bundle={bundle} />
      <section className="strategy-grid">
        {bundle.advisor.opportunity_rankings.map((item) => {
          const advice = bundle.advisor.games.find((game) => game.game_key === item.game_key);
          return (
            <article className="strategy-card" key={item.game_key}>
              <div className="strategy-top">
                <span className="rank-badge">#{item.rank}</span>
                <div>
                  <h2>{item.game_name}</h2>
                  <span>{item.recommendation_label}</span>
                </div>
              </div>
              <div className="strategy-metrics">
                <span>Prize</span>
                <strong>{money.format(item.prize_amount)}</strong>
                <span>Top odds</span>
                <strong>{formatOdds(item.jackpot_probability)}</strong>
                <span>Any prize</span>
                <strong>{percent.format(item.any_prize_probability)}</strong>
                <span>Source grade</span>
                <strong>{advice?.data_status.source_grade_label ?? "Not graded"}</strong>
              </div>
              <p>{item.reason}</p>
            </article>
          );
        })}
      </section>
      <ProjectionPanel bundle={bundle} />
      <CalibrationPanel bundle={bundle} />
      <BacktestPanel bundle={bundle} busy={busy} onRunBacktest={onRunBacktest} />
    </div>
  );
}

function HistoryTab({
  bundle,
  token,
  onCheckedResults,
  onSavedResult
}: {
  bundle: AppBundle;
  token: string;
  onCheckedResults: (payload: { history: TicketHistoryRecord[]; performance: AppBundle["performance"]; calibration?: AppBundle["calibration"] }) => void;
  onSavedResult: (ticket: TicketHistoryRecord) => void;
}) {
  const history = bundle.history;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const filteredHistory = history.filter((item) => {
    if (filter === "active") return item.lifecycle_status !== "checked";
    if (filter === "checked") return item.lifecycle_status === "checked";
    if (filter === "prize") return Boolean(item.result?.prize_name);
    return true;
  });
  if (!history.length) {
    return (
      <section className="empty-history">
        <History size={32} />
        <h1>No saved tickets yet.</h1>
        <p>Save today's three tickets to start the record.</p>
      </section>
    );
  }
  return (
    <section className="history-shell">
      <PerformancePanel bundle={bundle} />
      <SnapshotLedgerPanel bundle={bundle} />
      <div className="filter-row">
        {[
          ["all", "All"],
          ["active", "Active"],
          ["checked", "Checked"],
          ["prize", "Prize"]
        ].map(([key, label]) => (
          <button className={filter === key ? "selected" : ""} key={key} onClick={() => setFilter(key as HistoryFilter)}>
            {label}
          </button>
        ))}
      </div>
      <div className="history-actions">
        <button
          onClick={async () => {
            const checked = await checkTicketResults(token);
            onCheckedResults(checked);
          }}
        >
          <RefreshCw size={17} />
          Check results
        </button>
      </div>
      <div className="history-list">
        {filteredHistory.map((item) => {
        const key = `${item.game_key}:${item.draw_date}:${item.compact}`;
        const yourNumbers = compactToParts(item.compact);
        const winningNumbers = compactToParts(String(item.result?.winning_numbers ?? ""));
        return (
          <article className={`history-card game-${item.game_key}`} key={key}>
            <div className="history-card-top">
              <div>
                <span className="eyebrow">{item.draw_date}</span>
                <h2>{item.game_name}</h2>
              </div>
              <span className={`status-badge status-${item.lifecycle_status}`}>{item.lifecycle_status.replace("_", " ")}</span>
            </div>
            <div className="history-numbers">
              {yourNumbers?.whites.map((n) => (
                <NumberBall key={n} value={n} />
              ))}
              {yourNumbers && <NumberBall value={yourNumbers.bonus} bonus />}
            </div>
            <p>{item.explanation?.headline ?? "Tracked ticket"}</p>
            {item.data_status && (
              <div className="history-meta">
                {item.recommendation_id && <span>Saved reason {item.recommendation_id.slice(-6)}</span>}
                <span>{item.lifecycle_detail}</span>
                <span>{item.data_status.jackpot_source_label}</span>
                <span>{item.data_status.source_grade_label}</span>
              </div>
            )}
            {item.result?.prize_name !== undefined && (
              <div className="result-summary">
                <strong>{item.result.prize_name ? String(item.result.prize_name) : "No prize"}</strong>
                <span>
                  {String(item.result.white_hits ?? 0)} white
                  {item.result.bonus_hit ? " + bonus" : ""}
                </span>
              </div>
            )}
            {winningNumbers && (
              <div className="winning-row">
                <span>Published winning numbers</span>
                <div className="history-numbers">
                  {winningNumbers.whites.map((n) => (
                    <NumberBall key={n} value={n} />
                  ))}
                  <NumberBall value={winningNumbers.bonus} bonus />
                </div>
              </div>
            )}
            <details className="manual-result">
              <summary>Add private note</summary>
              <div className="result-entry">
                <input
                  value={drafts[key] ?? ""}
                  onChange={(event) => setDrafts({ ...drafts, [key]: event.target.value })}
                  placeholder="Optional note"
                />
                <button
                  onClick={async () => {
                    const saved = await saveTicketResult(token, item, { note: drafts[key] ?? "" });
                    onSavedResult(saved);
                  }}
                >
                  Save note
                </button>
              </div>
            </details>
          </article>
        );
        })}
      </div>
    </section>
  );
}

function SettingsTab({
  bundle,
  settings,
  setSettings,
  busy,
  onRefresh,
  onUpdate
}: {
  bundle: AppBundle;
  settings: AppSettings;
  setSettings: (next: AppSettings) => void;
  busy: boolean;
  onRefresh: () => void;
  onUpdate: () => void;
}) {
  function patch(next: Partial<AppSettings>) {
    setSettings({ ...settings, ...next });
  }
  return (
    <section className="settings-shell">
      <div className="settings-intro">
        <span className="eyebrow">Data</span>
        <h1>Updates run only when you request them.</h1>
        <p>Prize pulls, draw results, and ticket checks are no longer scheduled. Select Sync now whenever you want a fresh pull.</p>
      </div>
      <DataHealthPanel bundle={bundle} />
      <OutsideSignalsPanel bundle={bundle} />
      <LearningScopePanel bundle={bundle} />
      <SourceAuditPanel bundle={bundle} />
      <SourceFreshnessPanel bundle={bundle} />
      <SourceQualityPanel bundle={bundle} />
      <div className="settings-actions">
        <button onClick={onRefresh} disabled={busy}>
          <RefreshCw size={18} />
          Refresh view
        </button>
        <button onClick={onUpdate} disabled={busy}>
          <Activity size={18} />
          Sync now
        </button>
      </div>
      <div className="auto-sync-note">
        <ShieldCheck size={18} />
        <span>Cloudflare automatic updates are off.</span>
      </div>
      <details className="advanced-settings">
        <summary>Personal assumptions</summary>
        <div className="settings-grid">
          <label>
            Budget reference
            <input type="number" value={settings.budget} min={0} step={5} onChange={(event) => patch({ budget: Number(event.target.value) })} />
          </label>
          <label>
            After-tax keep rate
            <input type="number" value={settings.tax_keep_rate} min={0} max={1} step={0.01} onChange={(event) => patch({ tax_keep_rate: Number(event.target.value) })} />
          </label>
          <div className="private-status-box">
            <ShieldCheck size={18} />
            <span>Private session active. The app asks for the code again if it expires.</span>
          </div>
        </div>
      </details>
      <section className="sync-log-panel">
        <div className="panel-heading">
          <Activity size={20} />
          <div>
            <span>Sync log</span>
            <h2>{bundle.syncLog.length ? "Recent source checks" : "No sync receipts yet"}</h2>
          </div>
        </div>
        <div className="freshness-list">
          {bundle.syncLog.slice(0, 10).map((event) => (
            <article className={`freshness-row status-${event.status === "failure" ? "missing" : event.status === "warning" ? "stale" : "current"}`} key={`${event.source_key}:${event.observed_at}`}>
              <div>
                <strong>{event.source_key.replace(/_/g, " ")}</strong>
                <span>{event.detail}</span>
              </div>
              <small>{formatObserved(event.observed_at)}</small>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function UnlockScreen({
  busy,
  notice,
  onUnlock
}: {
  busy: boolean;
  notice: string;
  onUnlock: (code: string) => void;
}) {
  const [draft, setDraft] = useState("");
  return (
    <main className="unlock-screen">
      <section className="unlock-panel">
        <div className="brand-lockup">
          <img src="/icons/icon.svg" alt="" />
          <div>
            <strong>Lotto</strong>
            <span>{notice}</span>
          </div>
        </div>
        <div className="unlock-copy">
          <LockKeyhole size={28} />
          <h1>Unlock live tickets.</h1>
          <p>
            The public app shell is installed on your phone, but the ticket engine is private. Enter the 6-digit code once on this device to load live jackpots, saved ticket history, and the current three-game recommendation.
          </p>
        </div>
        <label>
          6-digit code
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={draft}
            onChange={(event) => setDraft(event.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                onUnlock(draft);
              }
            }}
            placeholder="000000"
          />
        </label>
        <button
          className="unlock-button"
          disabled={busy || draft.length !== 6}
          onClick={() => {
            onUnlock(draft);
          }}
        >
          <ShieldCheck size={18} />
          <span>{busy ? "Checking" : "Unlock"}</span>
        </button>
        <p className="unlock-footnote">The app stores a private session cookie after login, not the code itself.</p>
      </section>
    </main>
  );
}

export function App() {
  const [settings, setSettingsState] = useState<AppSettings>(loadSettings);
  const [token, setTokenState] = useState(readToken);
  const [bundle, setBundle] = useState<AppBundle | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("Loading");
  const [authRequired, setAuthRequired] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("tickets");
  const [selectedGame, setSelectedGame] = useState<GameKey>("powerball");

  function setSettings(next: AppSettings) {
    setSettingsState(next);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
    localStorage.removeItem(LEGACY_SETTINGS_KEY);
  }

  function setToken(next: string) {
    setTokenState(next);
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }

  async function refresh(tokenOverride = token) {
    setBusy(true);
    try {
      const auth = await getAuthStatus(tokenOverride);
      if (auth.protected && !auth.authorized) {
        setBundle(null);
        setAuthRequired(true);
        setNotice(tokenOverride ? "Private access rejected" : "Private login required");
        return;
      }
      const loaded = await loadAppBundle(settings, tokenOverride);
      setBundle(loaded);
      setAuthRequired(false);
      setNotice("Live data connected");
    } catch (error) {
      if (error instanceof ApiError && [401, 403].includes(error.status)) {
        setBundle(null);
        setAuthRequired(true);
        setNotice("Private login required");
        return;
      }
      setAuthRequired(false);
      setNotice("Connection needs refresh");
    } finally {
      setBusy(false);
    }
  }

  async function login(code: string) {
    setBusy(true);
    try {
      await loginWithCode(code);
      setToken("");
      setNotice("Live data connected");
      await refresh("");
    } catch {
      setBundle(null);
      setAuthRequired(true);
      setNotice("Code not accepted");
    } finally {
      setBusy(false);
    }
  }

  async function updateData() {
    setBusy(true);
    try {
      const result = await runLiveUpdate(token);
      const queued = (result.update as { status?: string } | undefined)?.status === "queued";
      setNotice(queued ? "Live refresh queued" : "Live data updated");
      if (queued) {
        setBusy(false);
        return;
      }
      await refresh();
    } catch {
      setNotice("Sync needs retry");
      setBusy(false);
    }
  }

  async function playTicket(advice: GameAdvice) {
    if (!bundle) return;
    setBusy(true);
    try {
      const ticket = await markTicketPlayed(token, advice);
      const nextHistory = [ticket, ...bundle.history.filter((row) => !(row.game_key === ticket.game_key && row.draw_date === ticket.draw_date && row.compact === ticket.compact))];
      setBundle({
        ...bundle,
        advisor: {
          ...bundle.advisor,
          games: bundle.advisor.games.map((game) => (game.game_key === advice.game_key ? { ...game, already_tracked: true } : game))
        },
        history: nextHistory,
        performance: buildPerformanceSummary(nextHistory),
        calibration: bundle.calibration,
        source: "live"
      });
      setNotice(`${advice.game_name} marked played`);
    } catch {
      setNotice("Tracking needs private login");
    } finally {
      setBusy(false);
    }
  }

  async function trackAllTickets() {
    if (!bundle) return;
    setBusy(true);
    try {
      const payload = await markAllTicketsPlayed(token, bundle.advisor.games);
      const saved = new Set(payload.history.map((item) => `${item.game_key}:${item.draw_date}:${item.compact}`));
      setBundle({
        ...bundle,
        advisor: {
          ...bundle.advisor,
          games: bundle.advisor.games.map((game) => ({ ...game, already_tracked: saved.has(trackedKey(game)) || game.already_tracked }))
        },
        history: payload.history,
        performance: payload.performance,
        calibration: payload.calibration ?? bundle.calibration,
        source: "live"
      });
      setNotice("All three marked played");
    } catch {
      setNotice("Tracking needs private login");
    } finally {
      setBusy(false);
    }
  }

  async function restoreLatestTickets() {
    if (!bundle) return;
    setBusy(true);
    try {
      const payload = await restoreLatestSnapshot(token);
      const saved = new Set(payload.history.map((item) => `${item.game_key}:${item.draw_date}:${item.compact}`));
      setBundle({
        ...bundle,
        advisor: {
          ...bundle.advisor,
          games: bundle.advisor.games.map((game) => ({ ...game, already_tracked: saved.has(trackedKey(game)) || game.already_tracked }))
        },
        history: payload.history,
        performance: payload.performance,
        calibration: payload.calibration ?? bundle.calibration,
        source: "live"
      });
      setNotice("Latest three tickets restored");
    } catch {
      setNotice("No restorable ticket receipt found");
    } finally {
      setBusy(false);
    }
  }

  async function runHistoricalTest() {
    if (!bundle) return;
    setBusy(true);
    try {
      const backtests = await runBacktest(token, settings, 20);
      setBundle({ ...bundle, backtests });
      setNotice("Replay updated");
    } catch {
      setNotice("Replay needs stored results");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const tracked = useMemo(
    () => new Set((bundle?.history ?? []).filter((item) => item.status !== "planned").map((item) => `${item.game_key}:${item.draw_date}:${item.compact}`)),
    [bundle]
  );

  if (!bundle && authRequired) {
    return <UnlockScreen busy={busy} notice={notice} onUnlock={(code) => void login(code)} />;
  }

  if (!bundle) {
    return (
      <main className="loading-screen">
        <img src="/icons/icon.svg" alt="" />
        <span>{notice === "Loading" ? "Loading Lotto" : notice}</span>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <img src="/icons/icon.svg" alt="" />
          <div>
            <strong>Lotto</strong>
            <span>{notice}</span>
          </div>
        </div>
        <div className="top-summary">
          <StatPill icon={Ticket} label="Tickets" value="3" />
          <StatPill icon={CircleDollarSign} label="Cost" value={money2.format(bundle.advisor.total_cost)} />
          <StatPill icon={ShieldCheck} label="Grade" value={bundle.advisor.ai_audit?.grade ?? "Building"} />
        </div>
      </header>

      <nav className="tabbar" aria-label="Primary">
        {[
          ["tickets", Ticket, "Tickets"],
          ["details", Info, "Why"],
          ["strategy", BarChart3, "Review"],
          ["history", History, "Ledger"],
          ["settings", SlidersHorizontal, "Data"]
        ].map(([key, Icon, label]) => {
          const TabIcon = Icon as LucideIcon;
          return (
            <button className={activeTab === key ? "active" : ""} key={key as string} onClick={() => setActiveTab(key as TabKey)}>
              <TabIcon size={18} />
              <span>{label as string}</span>
            </button>
          );
        })}
      </nav>

      {activeTab === "tickets" && (
        <TicketsTab
          bundle={bundle}
          selectedGame={selectedGame}
          setSelectedGame={setSelectedGame}
          tracked={tracked}
          openDetails={(game) => {
            setSelectedGame(game);
            setActiveTab("details");
          }}
          onMarkPlayed={playTicket}
          onTrackAll={trackAllTickets}
          onRestoreLatest={restoreLatestTickets}
          busy={busy}
        />
      )}
      {activeTab === "details" && <DetailsTab bundle={bundle} selectedGame={selectedGame} setSelectedGame={setSelectedGame} />}
      {activeTab === "strategy" && <StrategyTab bundle={bundle} busy={busy} onRunBacktest={runHistoricalTest} />}
      {activeTab === "history" && (
        <HistoryTab
          bundle={bundle}
          token={token}
          onCheckedResults={(payload) =>
            setBundle({
              ...bundle,
              history: payload.history,
              performance: payload.performance,
              calibration: payload.calibration ?? bundle.calibration,
              snapshots: bundle.snapshots
            })
          }
          onSavedResult={(ticket) =>
            {
              const nextHistory = bundle.history.map((item) =>
                item.game_key === ticket.game_key && item.draw_date === ticket.draw_date && item.compact === ticket.compact ? ticket : item
              );
              setBundle({
              ...bundle,
              history: nextHistory,
              performance: buildPerformanceSummary(nextHistory),
              calibration: bundle.calibration
            });
            }
          }
        />
      )}
      {activeTab === "settings" && (
        <SettingsTab
          bundle={bundle}
          settings={settings}
          setSettings={setSettings}
          busy={busy}
          onRefresh={refresh}
          onUpdate={updateData}
        />
      )}
    </main>
  );
}
